import { useState, useRef } from 'react';
import { importApi, organizationApi } from '../services/api';
import { Organization } from '@dsgvo-buddy/types';
import { useEffect } from 'react';
import {
  Title,
  Text,
  Button,
  FlexBox,
  Card,
  CardHeader,
  Icon,
  BusyIndicator,
  MessageStrip,
  Table,
  TableColumn,
  TableRow,
  TableCell,
  ObjectStatus
} from '@ui5/webcomponents-react';
import '@ui5/webcomponents-icons/dist/AllIcons.js';
import PageIntro from '../components/PageIntro';
import { pageIntros } from '../helpers/helpTexts';

interface ImportItem {
  action: 'create' | 'update' | 'skip' | 'error';
  type: string;
  name: string;
  reason?: string;
  sourceId?: number;
  targetId?: number;
}

interface ImportResult {
  schemaVersion: string;
  valid: boolean;
  items: ImportItem[];
  summary: {
    create: number;
    update: number;
    skip: number;
    error: number;
    total: number;
  };
}

const actionConfig: Record<string, { label: string; state: string }> = {
  create: { label: 'Neu anlegen', state: 'Success' },
  update: { label: 'Aktualisieren', state: 'Information' },
  skip: { label: 'Überspringen', state: 'None' },
  error: { label: 'Fehler', state: 'Error' }
};

const typeLabels: Record<string, string> = {
  verarbeitung: 'Verarbeitung',
  dienstleister: 'Dienstleister',
  tom: 'TOM-Maßnahme',
  hinweis: 'Datenschutzhinweis',
  schema: 'Schema'
};

export default function ImportPage() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentOrg, setCurrentOrg] = useState<Organization | null>(null);
  const [importData, setImportData] = useState<any>(null);
  const [fileName, setFileName] = useState('');
  const [dryRunResult, setDryRunResult] = useState<ImportResult | null>(null);
  const [importComplete, setImportComplete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrg();
  }, []);

  const loadOrg = async () => {
    const res = await organizationApi.getAll();
    if (res.success && res.data && res.data.length > 0) {
      setCurrentOrg(res.data[0]);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    setDryRunResult(null);
    setImportComplete(false);
    setFileName(file.name);

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      setImportData(data);
    } catch {
      setError('Datei konnte nicht gelesen werden. Bitte eine gültige JSON-Datei auswählen.');
      setImportData(null);
    }
  };

  const handleDryRun = async () => {
    if (!currentOrg || !importData) return;
    setLoading(true);
    setError(null);

    const res = await importApi.dryRun(currentOrg.id, importData);
    if (res.success && res.data) {
      setDryRunResult(res.data);
    } else {
      setError(res.error || 'Dry-Run fehlgeschlagen');
    }
    setLoading(false);
  };

  const handleImport = async () => {
    if (!currentOrg || !importData || !dryRunResult?.valid) return;
    setLoading(true);
    setError(null);

    const res = await importApi.execute(currentOrg.id, importData);
    if (res.success && res.data) {
      setDryRunResult(res.data);
      setImportComplete(true);
    } else {
      setError(res.error || 'Import fehlgeschlagen');
    }
    setLoading(false);
  };

  const handleReset = () => {
    setImportData(null);
    setFileName('');
    setDryRunResult(null);
    setImportComplete(false);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="page-layout--form">
      <FlexBox justifyContent="SpaceBetween" alignItems="Center" style={{ marginBottom: '1.5rem' }}>
        <div>
          <Title level="H3" style={{ marginBottom: '0.25rem' }}>JSON-Import</Title>
          <Text style={{ color: 'var(--sapNeutralTextColor)' }}>
            Datenschutz-Objekte aus einer Exportdatei importieren
          </Text>
        </div>
      </FlexBox>

      <PageIntro {...pageIntros.import} storageKey="import" />

      {error && (
        <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
          {error}
        </MessageStrip>
      )}

      {importComplete && (
        <MessageStrip design="Positive" style={{ marginBottom: '1rem' }}>
          Import erfolgreich abgeschlossen! {dryRunResult?.summary.create} neu erstellt, {dryRunResult?.summary.update} aktualisiert.
        </MessageStrip>
      )}

      {/* Step 1: File Upload */}
      <Card
        style={{ marginBottom: '1rem' }}
        header={
          <CardHeader
            titleText="1. Datei auswählen"
            subtitleText={fileName || 'Noch keine Datei ausgewählt'}
            avatar={<Icon name="upload" />}
          />
        }
      >
        <div style={{ padding: '1rem 1.25rem 1.25rem' }}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <FlexBox alignItems="Center" style={{ gap: '0.75rem' }}>
            <Button
              icon="upload"
              design="Default"
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
            >
              JSON-Datei auswählen
            </Button>
            {importData && (
              <>
                <Text style={{ color: 'var(--sapPositiveTextColor)', fontWeight: '600' }}>
                  {fileName} geladen
                </Text>
                <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem' }}>
                  (Schema v{importData.schemaVersion}, Export vom {importData.exportedAt ? new Date(importData.exportedAt).toLocaleDateString('de-DE') : '-'})
                </Text>
              </>
            )}
          </FlexBox>
        </div>
      </Card>

      {/* Step 2: Dry Run */}
      {importData && !importComplete && (
        <Card
          style={{ marginBottom: '1rem' }}
          header={
            <CardHeader
              titleText="2. Vorschau (Dry-Run)"
              subtitleText="Prüfen Sie, welche Objekte importiert werden"
              avatar={<Icon name="inspection" />}
              action={
                !dryRunResult ? (
                  <Button icon="simulate" design="Emphasized" onClick={handleDryRun} disabled={loading}>
                    {loading ? 'Wird analysiert...' : 'Dry-Run starten'}
                  </Button>
                ) : undefined
              }
            />
          }
        >
          {loading && !dryRunResult && (
            <FlexBox alignItems="Center" justifyContent="Center" style={{ padding: '2rem' }}>
              <BusyIndicator active size="Large" />
            </FlexBox>
          )}

          {dryRunResult && (
            <div style={{ padding: '0 1.25rem 1.25rem' }}>
              {/* Summary */}
              <FlexBox wrap="Wrap" style={{ gap: '1rem', marginBottom: '1rem' }}>
                <div style={{ padding: '0.75rem 1rem', borderRadius: '0.375rem', background: 'var(--sapPositiveBackground, #f1fdf6)', flex: '1 1 120px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--sapPositiveColor)' }}>{dryRunResult.summary.create}</Text>
                  <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>Neu</Text>
                </div>
                <div style={{ padding: '0.75rem 1rem', borderRadius: '0.375rem', background: 'var(--sapInformativeBackground, #e8f0fe)', flex: '1 1 120px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--sapInformativeColor)' }}>{dryRunResult.summary.update}</Text>
                  <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>Aktualisiert</Text>
                </div>
                <div style={{ padding: '0.75rem 1rem', borderRadius: '0.375rem', background: 'var(--sapNeutralBackground, #f5f6f7)', flex: '1 1 120px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '1.5rem', fontWeight: '700', color: 'var(--sapNeutralColor)' }}>{dryRunResult.summary.skip}</Text>
                  <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>Übersprungen</Text>
                </div>
                <div style={{ padding: '0.75rem 1rem', borderRadius: '0.375rem', background: dryRunResult.summary.error > 0 ? 'var(--sapErrorBackground, #fef2f2)' : 'var(--sapNeutralBackground, #f5f6f7)', flex: '1 1 120px', textAlign: 'center' }}>
                  <Text style={{ fontSize: '1.5rem', fontWeight: '700', color: dryRunResult.summary.error > 0 ? 'var(--sapNegativeColor)' : 'var(--sapNeutralColor)' }}>{dryRunResult.summary.error}</Text>
                  <Text style={{ display: 'block', fontSize: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>Fehler</Text>
                </div>
              </FlexBox>

              {/* Items Table */}
              {dryRunResult.items.length > 0 && (
                <Table
                  style={{ width: '100%' }}
                  columns={
                    <>
                      <TableColumn><Text style={{ fontWeight: '600' }}>Aktion</Text></TableColumn>
                      <TableColumn><Text style={{ fontWeight: '600' }}>Typ</Text></TableColumn>
                      <TableColumn><Text style={{ fontWeight: '600' }}>Name</Text></TableColumn>
                      <TableColumn demandPopin minWidth={500}><Text style={{ fontWeight: '600' }}>Details</Text></TableColumn>
                    </>
                  }
                >
                  {dryRunResult.items.map((item, idx) => (
                    <TableRow key={idx}>
                      <TableCell>
                        <ObjectStatus state={actionConfig[item.action]?.state as any} showDefaultIcon>
                          {actionConfig[item.action]?.label}
                        </ObjectStatus>
                      </TableCell>
                      <TableCell>
                        <Text>{typeLabels[item.type] || item.type}</Text>
                      </TableCell>
                      <TableCell>
                        <Text style={{ fontWeight: '600' }}>{item.name}</Text>
                      </TableCell>
                      <TableCell>
                        <Text style={{ color: 'var(--sapNeutralTextColor)', fontSize: '0.8125rem' }}>
                          {item.reason || (item.targetId ? `→ ID ${item.targetId}` : '')}
                        </Text>
                      </TableCell>
                    </TableRow>
                  ))}
                </Table>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Step 3: Execute */}
      {dryRunResult && !importComplete && (
        <Card
          header={
            <CardHeader
              titleText="3. Import ausführen"
              subtitleText={dryRunResult.valid ? 'Bereit zum Import' : 'Import nicht möglich — Fehler beheben'}
              avatar={<Icon name="accept" />}
            />
          }
        >
          <div style={{ padding: '1rem 1.25rem 1.25rem' }}>
            {!dryRunResult.valid && (
              <MessageStrip design="Negative" style={{ marginBottom: '1rem' }}>
                Der Import enthält Fehler. Bitte korrigieren Sie die Exportdatei und versuchen Sie es erneut.
              </MessageStrip>
            )}
            <FlexBox style={{ gap: '0.75rem' }}>
              <Button
                icon="accept"
                design="Emphasized"
                onClick={handleImport}
                disabled={!dryRunResult.valid || loading}
              >
                {loading ? 'Wird importiert...' : 'Import jetzt ausführen'}
              </Button>
              <Button icon="decline" design="Transparent" onClick={handleReset}>
                Abbrechen
              </Button>
            </FlexBox>
          </div>
        </Card>
      )}

      {/* Reset after complete */}
      {importComplete && (
        <Card>
          <div style={{ padding: '1.25rem', textAlign: 'center' }}>
            <Button icon="upload" design="Default" onClick={handleReset}>
              Weiteren Import starten
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
